import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PinwheelModal from '@pinwheel/react-modal';

import { formatPrice } from 'common/formatters';
import {
  INCOME_VERIFICATION_STATUS_PENDING,
  INCOME_VERIFICATION_STATUS_COMPLETED,
  INCOME_VERIFICATION_STATUS_CREATED,
  INCOME_VERIFICATION_STATUS_STARTED,
  INCOME_VERIFICATION_PROVIDER_PINWHEEL,
  INCOME_VERIFICATION_DEFAULT_PROVIDER,
  INCOME_VERIFICATION_PROVIDER_NAME_MAPPING,
  INCOME_VERIFICATION_STATUS_FAILED,
} from 'common/incomeVerificationConstants';

import { EMPLOYMENT_VERIFICATION_STATUS_FAILED } from 'common/employmentVerificationConstants';

import { scrollToFormField } from 'client/helpers/formikHelpers';

import IncomeVerificationActions from 'client/actions/IncomeVerification';
import EmploymentVerificationActions from 'client/actions/EmploymentVerification';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import ButtonDeprecated, { BUTTON_THEME_BLUE_GREYCLIFF } from 'client/components/ButtonDeprecated';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrPayrollCard from 'client/components/SrPayrollCard/SrPayrollCard';
import SrCollapsibleOption from 'client/components/SrCollapsibleOption/SrCollapsibleOption';
import moment from 'moment';

const INCOME_VERIFICATION_FIELD_ID = 'incomeVerificationVOIE';
const PINWHEEL_EVENT_CLOSE = 'close';
const PINWHEEL_EVENT_EXIT = 'exit';

const SrRentalApplicationVerifyIncome = ({
  dispatch,
  listing,
  currentUser,
  householdApplicationId,
  incomeVerification,
  retryIncomeVerificationFailed,
  reloadIncomeVerificationData,
  onRetryIncomeVerification,
  onIframeClose,
  isVerificationOptionsUnderstood,
  validateAcknowledge,
}) => {
  const [incomeVerificationScreeningIframeUrl, setIncomeVerificationScreeningIframeUrl] = useState(
    '',
  );
  const [manualEdit, setManualEdit] = useState(false);

  useEffect(() => {
    const timer = setInterval(reloadIncomeVerificationData, 3000);
    return () => clearInterval(timer);
  }, [reloadIncomeVerificationData]);

  useEffect(() => {
    // If the user request manual re-connection should create a new income verification.
    if (retryIncomeVerificationFailed) {
      onEditIncomeVerification();
    }
  }, [retryIncomeVerificationFailed, onEditIncomeVerification]);

  useEffect(() => {
    // Listing income verification status to close the modal
    if (
      incomeVerificationScreeningIframeUrl &&
      incomeVerification &&
      incomeVerification.status !== INCOME_VERIFICATION_STATUS_CREATED &&
      incomeVerification.status !== INCOME_VERIFICATION_STATUS_STARTED &&
      !manualEdit &&
      !retryIncomeVerificationFailed
    ) {
      setIncomeVerificationScreeningIframeUrl('');
      scrollToFormField({ formFieldId: INCOME_VERIFICATION_FIELD_ID });
    }
  }, [
    incomeVerification,
    incomeVerificationScreeningIframeUrl,
    manualEdit,
    retryIncomeVerificationFailed,
  ]);

  const onEditIncomeVerification = useCallback(async () => {
    setManualEdit(true);
    await verifyIncome(true);
  }, [verifyIncome]);

  const verifyIncome = useCallback(
    async (isEdit = false) => {
      if (!isEdit && isVerificationOptionsUnderstood) {
        return validateAcknowledge();
      }

      let verification = await dispatch(IncomeVerificationActions.getByApplicationId, {
        householdApplicationId,
        reload: true,
      });

      const employmentVerification = await dispatch(
        EmploymentVerificationActions.getByApplicationId,
        {
          householdApplicationId,
        },
      );

      if (
        !employmentVerification ||
        employmentVerification?.status === EMPLOYMENT_VERIFICATION_STATUS_FAILED
      ) {
        await dispatch(EmploymentVerificationActions.create, {
          userId: parseInt(currentUser.id, 10),
        });
      }

      // If there is no income verification or manually requested
      // to connect again cause FAILED, then create a new one.
      if (!verification || retryIncomeVerificationFailed || isEdit) {
        const incomeVerificationGroup = await dispatch(
          IncomeVerificationActions.createScreeningGroup,
          {
            userIds: [currentUser.id],
            listingId: listing.id,
          },
        );

        verification = incomeVerificationGroup.incomeVerifications?.find(
          (incomeVerification) => incomeVerification.userId === currentUser.id,
        );
      }

      if (verification) {
        const screeningUrl = await dispatch(IncomeVerificationActions.getScreeningUrl, {
          incomeVerificationId: verification.id,
        });

        setIncomeVerificationScreeningIframeUrl(screeningUrl);
      }
    },
    [
      currentUser,
      dispatch,
      listing,
      retryIncomeVerificationFailed,
      isVerificationOptionsUnderstood,
      validateAcknowledge,
      householdApplicationId,
    ],
  );

  const onCloseModal = () => {
    setIncomeVerificationScreeningIframeUrl('');
    setManualEdit(false);
    onRetryIncomeVerification(false);
    reloadIncomeVerificationData();
    if (onIframeClose) {
      onIframeClose();
    }
  };

  const onPinwheelEvent = (pinwheelEvent) => {
    if (pinwheelEvent === PINWHEEL_EVENT_CLOSE || pinwheelEvent === PINWHEEL_EVENT_EXIT) {
      onCloseModal();
    }
  };

  const incomeProvider = incomeVerification?.provider || INCOME_VERIFICATION_DEFAULT_PROVIDER;
  const providerName = INCOME_VERIFICATION_PROVIDER_NAME_MAPPING[incomeProvider] || 'Provider';

  let incomeVerificationBody = (
    <SrIncomeProviderCollapsable
      isVerificationOptionsUnderstood={isVerificationOptionsUnderstood}
      validateAcknowledge={validateAcknowledge}
    >
      <SrIncomeVerificationNotStartedBody
        isVerificationOptionsUnderstood={isVerificationOptionsUnderstood}
        onVerifyIncome={verifyIncome}
        providerName={providerName}
      />
    </SrIncomeProviderCollapsable>
  );

  if (incomeVerification?.status === INCOME_VERIFICATION_STATUS_PENDING) {
    incomeVerificationBody = (
      <SrIncomeProviderCollapsable
        isVerificationOptionsUndxerstood={isVerificationOptionsUnderstood}
        validateAcknowledge={validateAcknowledge}
      >
        <SrIncomeVerificationPendingBody />
      </SrIncomeProviderCollapsable>
    );
  } else if (incomeVerification?.status === INCOME_VERIFICATION_STATUS_COMPLETED) {
    const { paystubs = [], currentJobStartDate, currentJobCompany } = incomeVerification;
    const startDate = currentJobStartDate ? moment(currentJobStartDate).format('MM/DD/YY') : '-';
    const reportData = [
      { label: 'Monthly Income', value: formatPrice(incomeVerification.incomeMonthly) },
      { label: currentJobCompany ? 'Employer' : '', value: currentJobCompany || '' },
      { label: 'Paystubs collected', value: paystubs?.length },
      { label: 'Start Date', value: startDate },
    ];
    incomeVerificationBody = (
      <SrPayrollCard
        incomeVerificationId={incomeVerification.id}
        reportData={reportData}
        onClick={onEditIncomeVerification}
      />
    );
  }

  if (incomeVerification?.status === INCOME_VERIFICATION_STATUS_FAILED) {
    return null;
  }

  return (
    <div id={`field-${INCOME_VERIFICATION_FIELD_ID}`}>
      {incomeVerificationBody}
      {incomeVerificationScreeningIframeUrl && (
        <SrIncomeProviderModal
          onCloseModal={onCloseModal}
          incomeProvider={incomeProvider}
          onPinwheelEvent={onPinwheelEvent}
          incomeVerificationScreeningIframeUrl={incomeVerificationScreeningIframeUrl}
        />
      )}
    </div>
  );
};

const SrIncomeProviderCollapsable = ({
  isVerificationOptionsUnderstood,
  validateAcknowledge,
  children,
}) => {
  return (
    <SrCollapsibleOption
      title="Link your payroll provider"
      label="For employees with direct deposit"
      buttonText="Verify Income"
      isActive={!isVerificationOptionsUnderstood}
      icon="payroll-icon"
      onClick={validateAcknowledge}
      changeBackground
      initialCollapsedState={false}
    >
      {children}
    </SrCollapsibleOption>
  );
};

const SrIncomeProviderModal = ({
  onCloseModal,
  incomeProvider,
  onPinwheelEvent,
  incomeVerificationScreeningIframeUrl,
}) => {
  if (incomeProvider === INCOME_VERIFICATION_PROVIDER_PINWHEEL) {
    return (
      <SrBox position="relative">
        <SrBox>
          <PinwheelModal
            linkToken={incomeVerificationScreeningIframeUrl}
            onEvent={onPinwheelEvent}
            open
          />
        </SrBox>
      </SrBox>
    );
  }

  return (
    <SrDarkModal onClose={onCloseModal}>
      <SrModalContent
        style={{ width: '100%', height: '100%' }}
        innerStyle={{ height: '100%', overflow: 'hidden' }}
        onCloseIconClick={onCloseModal}
      >
        <iframe
          title="Income Verification"
          src={incomeVerificationScreeningIframeUrl}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '4px',
          }}
        />
      </SrModalContent>
    </SrDarkModal>
  );
};

const SrIncomeVerificationNotStartedBody = ({
  isVerificationOptionsUnderstood,
  onVerifyIncome,
}) => {
  return (
    <>
      <SrText
        weight="500"
        size="16"
        lineHeight="24px"
        color={!isVerificationOptionsUnderstood ? null : 'greyMonochrome'}
      >
        Use this option:
        <br />
        <SrBox margin="12px 0" marginLeft="10px">
          {'\u2022 If you are employed and receive direct deposit paycheck to your bank account'}
        </SrBox>
        If your payroll provider isn't supported, you can manually upload documents below.
      </SrText>
      <SrBox margin="32px 0 16px">
        <ButtonDeprecated
          height="60px"
          onClick={() => onVerifyIncome(false)}
          theme={BUTTON_THEME_BLUE_GREYCLIFF}
        >
          Verify Income
        </ButtonDeprecated>
      </SrBox>
      <SrText
        weight="500"
        color={isVerificationOptionsUnderstood ? null : 'greyMonochrome'}
        size="16"
        lineHeight="18.45px"
        width="100%"
        display="block"
        align="center"
      >
        🔒 Private, secure, and encrypted
      </SrText>
    </>
  );
};

const SrIncomeVerificationPendingBody = () => {
  return (
    <>
      <SrFlex align="center" marginBottom="8px">
        <img
          alt="Clock"
          src="/images/clock-large-blue.svg"
          width="20px"
          height="20px"
          style={{ marginRight: 6 }}
        />
        <SrText font="whitney-sc" weight="800" lineHeight="21px">
          Pending verification...
        </SrText>
      </SrFlex>
      <SrText font="whitney-sc" weight="500" lineHeight="20px">
        This usually takes a few minutes
      </SrText>
    </>
  );
};

SrRentalApplicationVerifyIncome.propTypes = {
  dispatch: PropTypes.func.isRequired,
  listing: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  householdApplicationId: PropTypes.number.isRequired,
  incomeVerification: PropTypes.object,
  retryIncomeVerificationFailed: PropTypes.bool,
  onRetryIncomeVerification: PropTypes.func.isRequired,
  reloadIncomeVerificationData: PropTypes.func.isRequired,
  onIframeClose: PropTypes.func,
  validateAcknowledge: PropTypes.func,
  isVerificationOptionsUnderstood: PropTypes.bool,
};

SrRentalApplicationVerifyIncome.displayName = 'SrRentalApplicationVerifyIncome';

export default SrRentalApplicationVerifyIncome;
